import type {Dispatch, ReactElement, SetStateAction} from "react";
import {AnankeTempDisabledInlineEditableTextField} from "~/components/anankeTemp/anankeTempDisabledInlineEditableTextField";
import type {BtlActivity} from "~/domainModels/btlActivities";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeInlineEditableTextField} from "@vesta/ananke/components/anankeInlineEditableTextField";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {PlatformUserDisplayDetails} from "~/utilities/utilities";

export type AdditionalColumnTypeToDetailsMap<
    // Base AdditionalColumnType enum - must be a valid key type
    T extends string | number | symbol,
    // AdditionalColumnValueTypeMap - must be a record indexed by T
    U extends Record<T, unknown>,
    TStringValueContext extends object = {
        journeysModuleConfiguration: EventContainerTemplate;
    },
> = {
    [K in T]: {
        displayName: string;
        defaultValueGenerator: ({
            eventTemplates,
            organizationalUnits,
            organizationalUnitTemplates,
            users,
            userTemplates,
            btlActivities,
            products,
        }: {
            eventTemplates: Array<OttopilotEventTemplate>;
            organizationalUnits: Array<OrganizationalUnit>;
            organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
            users: Array<User>;
            userTemplates: Array<UserTemplate>;
            btlActivities: Array<BtlActivity>;
            products?: Array<ProductCatalogue>;
        }) => U[K];
        randomValueGenerator: ({
            eventTemplates,
            organizationalUnits,
            organizationalUnitTemplates,
            users,
            userTemplates,
            btlActivities,
            products,
        }: {
            eventTemplates: Array<OttopilotEventTemplate>;
            organizationalUnits: Array<OrganizationalUnit>;
            organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
            users: Array<User>;
            userTemplates: Array<UserTemplate>;
            btlActivities: Array<BtlActivity>;
            products?: Array<ProductCatalogue>;
        }) => U[K];
        /**
         * Convert to string for display in card, tables, etc
         */
        stringValueGetter: (args: {
            value: U[K];
            eventTemplate: OttopilotEventTemplate;
            additionalValueKey: Uuid;
            eventTemplates: Array<OttopilotEventTemplate>;
            organizationalUnits: Array<OrganizationalUnit>;
            organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
            users: Array<User>;
            userTemplates: Array<UserTemplate>;
            platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
            btlActivities: Array<BtlActivity>;
            products?: Array<ProductCatalogue>;
            userDefinedObjectOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
        } & TStringValueContext) => string;
        /**
         * Non-editable form component that can be wrapped inside AnankeField for view pages
         */
        displayComponent: ({value}: {value: U[K]}) => ReactElement;
        /**
         * Editable form component that can be wrapped inside AnankeField for create pages
         */
        editComponent: ({
            value,
            setValue,
            options,
            organizationalUnitId,
            productCatalogueTemplateId,
            allowedProductIds,
        }: {
            value: U[K];
            setValue: Dispatch<SetStateAction<U[K]>>;
            options?: Array<{id: U[K]; displayName: string}>;
            organizationalUnitId?: Uuid | null;
            productCatalogueTemplateId?: Uuid;
            allowedProductIds?: ReadonlySet<Uuid>;
        }) => ReactElement;
        /**
         * Editable form component that can be wrapped inside AnankeField for edit pages
         */
        inlineEditComponent: ({
            value,
            setValue,
            options,
            defaultValue,
            organizationalUnitId,
            productCatalogueTemplateId,
            allowedProductIds,
        }: {
            value: U[K];
            setValue: Dispatch<SetStateAction<U[K]>>;
            options?: Array<{id: U[K]; displayName: string}>;
            defaultValue?: {
                value: U[K];
                label: string;
            };
            organizationalUnitId?: Uuid | null;
            productCatalogueTemplateId?: Uuid;
            allowedProductIds?: ReadonlySet<Uuid>;
        }) => ReactElement;
    };
};

export function getProductCatalogueTemplateIdFromAdditionalColumnConfiguration(configuration: unknown): Uuid | undefined {
    if (configuration === null || typeof configuration !== "object") {
        return undefined;
    }
    if ("productCatalogueTemplateId" in configuration === false) {
        return undefined;
    }
    const id = (configuration as {productCatalogueTemplateId?: Uuid}).productCatalogueTemplateId;
    return id;
}

export type AdditionalColumnOption = {displayName: string; id: Uuid};

/** Resolve the options belonging to an additional column's referenced UDO template. */
export function getAdditionalColumnOptions(
    column: unknown,
    userDefinedObjectOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<AdditionalColumnOption>>,
): Array<AdditionalColumnOption> | undefined {
    if (column === null || typeof column !== "object" || "configuration" in column === false) {
        return undefined;
    }
    if (column.configuration === null || typeof column.configuration !== "object") {
        return undefined;
    }
    if ("userDefinedObjectTemplateId" in column.configuration && typeof column.configuration.userDefinedObjectTemplateId === "string") {
        return [...(userDefinedObjectOptionsByTemplateId?.get(column.configuration.userDefinedObjectTemplateId as Uuid) ?? [])];
    }
    return undefined;
}

export function additionalColumn_text_stringValueGetter({value}: {value: string}) {
    return value;
}

export function additionalColumn_text_viewComponent({value}: {value: string}) {
    // return <div>
    //     {value}
    // </div>;
    return <AnankeTempDisabledInlineEditableTextField value={value} />;
}

export function additionalColumn_text_editComponent({value, setValue}: {value: string; setValue: Dispatch<SetStateAction<string>>}) {
    return (
        <AnankeTextField
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
        />
    );
}

export function additionalColumn_text_inlineEditComponent({value, setValue}: {value: string; setValue: Dispatch<SetStateAction<string>>}) {
    return (
        <AnankeInlineEditableTextField
            defaultValue={value}
            readViewFitContainerWidth={true}
            onConfirm={(value) => {
                // if (value === journey.additionalColumnValues[column.id]) {
                //     return;
                // }
                setValue(value);
            }}
            // placeholder={`Enter ${column.displayName}`}
            // placeholder={column.displayName}
            placeholder="No value"
            // hideActionButtons={true}
        />
    );
}

export function additionalColumn_select_stringValueGetter({
    value,
    options,
}: {
    value: Uuid | null;
    options: Array<{
        id: Uuid;
        label: string;
    }>;
}) {
    if (value === null) {
        return "-";
    }

    const option = options.find((option) => option.id === value);
    if (option === undefined) {
        return "?";
    }

    return option.label;
}

export function additionalColumn_select_viewComponent({
    value,
    options,
}: {
    value: Uuid | null;
    options: Array<{
        id: Uuid;
        label: string;
    }>;
}) {
    const stringValue = additionalColumn_select_stringValueGetter({value, options});

    // Not sure if this is the best way to do this, but this is the way I'm going with for now
    return <AnankeTempDisabledInlineEditableTextField value={stringValue} />;
}

export function additionalColumn_select_editComponent({
    value,
    setValue,
    options,
}: {
    value: Uuid;
    setValue: Dispatch<SetStateAction<Uuid>>;
    options: Array<{
        id: Uuid;
        label: string;
    }>;
}) {
    const stringValue = additionalColumn_select_stringValueGetter({value, options});

    return (
        <AnankeSelect
            value={{
                id: value,
                label: stringValue,
            }}
            onChange={(newValue: {id: Uuid; label: string} | null) => {
                if (newValue !== null) {
                    setValue(newValue.id);
                }
            }}
            options={options.map((option) => ({
                id: option.id,
                label: option.label,
            }))}
        />
    );
}

export function additionalColumn_select_inlineEditComponent({value, setValue}: {value: string; setValue: Dispatch<SetStateAction<string>>}) {
    return (
        <AnankeInlineEditableTextField
            defaultValue={value}
            readViewFitContainerWidth={true}
            onConfirm={(value) => {
                // if (value === journey.additionalColumnValues[column.id]) {
                //     return;
                // }
                setValue(value);
            }}
            // placeholder={`Enter ${column.displayName}`}
            // placeholder={column.displayName}
            placeholder="No value"
            hideActionButtons={true}
        />
    );
}
